import { AuthProgressComponent } from '@shared/auth-progress/auth-progress.component'
import { Organization } from 'auth0'
import { combineLatest, defer, EMPTY, firstValueFrom, from, ReplaySubject, Subscription, throwError, timer } from 'rxjs'
import { catchError, filter, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators'

import { CommonModule } from '@angular/common'
import { HttpBackend, HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import { policyDeniedOrganization } from '@services/organization-sign-in.interceptor'
import { RuntimeCapabilitiesService } from '@services/runtime-capabilities.service'
import { Component, OnDestroy, inject } from '@angular/core'
import { AuthenticationService, isAccountDiscoveryUnavailable, isInteractiveAuthRequired, SsoMigrationRecoveryError, UserAccounts } from '@services/authentication.service'
import { SharedModule } from '@shared/shared.module'
import { environment } from 'src/environments/environment'

export function isLocalAuthPreviewHost(hostname: string): boolean {
  return ['localhost', '127.0.0.1'].includes(hostname)
    || hostname.endsWith('.local.kartorium.app')
}

@Component({
  imports: [CommonModule, SharedModule, AuthProgressComponent],
  selector: 'shared-login',
  standalone: true,
  styleUrls: ['./login.component.css'],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnDestroy {
  private static readonly LOCAL_PREVIEW_AUTH_REDIRECT_STARTED_KEY = 'kartorium_local_preview_auth_redirect_started_at'
  private static readonly LOCAL_PREVIEW_AUTH_REDIRECT_GRACE_MS = 30_000
  private static readonly LOCAL_PREVIEW_AUTH_REDIRECT_MAX_ATTEMPTS = 2

  public defaultLogo = 'assets/logo.png'
  public hasPersonalAccount = false
  public organizations: Organization[] = []
  public search = ''
  public state: 'loading' | 'selectAccount' | 'authRecovery' | 'accountRecovery' | 'migrateAccount' | 'accountNotProvisioned' | 'migrationRecovery' = 'loading'
  public signInError = ''
  public migrationError = ''
  public migrationInProgress = false
  public migrationRecovery: 'retryable' | 'uncertain' | 'terminal' = 'retryable'
  private policyOrganization?: string
  private readonly destroyed$ = new ReplaySubject<void>(1)
  private accountLoadRequested = false
  private migrationCompletionPending = false
  private authLoginRequested = false
  private authenticated = false
  private localPreviewRetry?: Subscription
  private authErrorSubscription: Subscription
  get currentUser$() { return this._authenticationService.user$ }

  constructor(
    private _authenticationService: AuthenticationService,
    private router: Router,
    private capabilities: RuntimeCapabilitiesService,
  ) {
    this.authErrorSubscription = this._authenticationService.error$.subscribe(error => {
      if (isInteractiveAuthRequired(error)) return
      this.signInError = 'We could not complete that sign-in. Please retry or contact support.'
      this.authLoginRequested = false
      this.state = 'authRecovery'
    })
    const params = new URLSearchParams(window.location.search)
    const policyOrganization = params.get('policy') === '1' ? params.get('organization') : sessionStorage.getItem('signInPolicyOrganization')
    if (policyOrganization && /^org_[A-Za-z0-9]+$/.test(policyOrganization)) {
      this.policyOrganization = policyOrganization
      sessionStorage.setItem('signInPolicyOrganization', policyOrganization)
      this.signInError = 'This organization requires a different sign-in method. Return to sign in with an allowed method, or choose another organization.'
    }
    const provider = new URLSearchParams(window.location.search).get('provider')
    if (provider !== null) {
      window.history.replaceState({}, '', '/login')
      const connection = provider === 'google' ? 'google-oauth2'
        : provider === 'microsoft' ? 'kartorium-microsoft' : null
      if (!connection) {
        this.signInError = 'Unsupported sign-in provider. Return to sign in and choose Google, Microsoft or your password.'
        this.state = 'authRecovery'
        return
      }
      this.authLoginRequested = true
      this._authenticationService.loginWithRedirect({
        appState: { target: 'login' }, authorizationParams: { connection },
      })
      return
    }
    const companyEmail = new URLSearchParams(window.location.search).get('company_email')
    if (companyEmail !== null) {
      // Remove routing hints before callback/reload. Never accept query org/connection.
      window.history.replaceState({}, '', '/login')
      const email = companyEmail.trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) {
        this.signInError = 'Enter a valid work email to continue with company SSO.'
        this.state = 'authRecovery'
        return
      }
      // Discovery is public and must work before a session exists. Use only the
      // raw backend here; protected API calls retain the normal token interceptors.
      new HttpClient(inject(HttpBackend)).get<{ connection: string | null, organization?: string }>(`${environment.api}/auth/sso/discover`,
        { params: { domain: email.slice(email.lastIndexOf('@') + 1).toLowerCase() } }).pipe(take(1)).subscribe({
        next: route => {
          if (!route.connection || !route.organization || !/^org_[A-Za-z0-9]+$/.test(route.organization)) {
            this.signInError = 'No unique company SSO was found. Contact your organization administrator.'
            this.state = 'authRecovery'
            return
          }
          this.authLoginRequested = true
          this._authenticationService.loginWithRedirect({ appState: { target: this.policyOrganization ? `login?policy=1&organization=${this.policyOrganization}` : 'login' }, authorizationParams: {
            organization: route.organization, connection: route.connection, login_hint: email,
          } })
        },
        error: () => {
          this.signInError = 'Company SSO is temporarily unavailable. Please retry.'
          this.state = 'authRecovery'
        },
      })
      return
    }
    if (this.isLocalPreview) {
      this.localPreviewRetry = timer(4_000, 4_000).subscribe(() => this.retryAuthLoginFromLocalPreview())
    }

    combineLatest([
      this._authenticationService.isLoading$,
      this._authenticationService.isAuthenticated$,
    ]).pipe(
      tap(([isLoading, isAuthenticated]) => {
        this.authenticated = isAuthenticated
        if (!isLoading) this.clearLocalPreviewAuthRedirectMarker()
      }),
      filter(([isLoading]) => !isLoading),
      take(1),
      switchMap(([, isAuthenticated]) => {
        if (!isAuthenticated) {
          this.navigateToAuthLogin()
          return EMPTY
        }

        return this.completeMigrationAndLoadAccounts()
      }),
      catchError(err => this.handleAccountLoadError(err)),
      takeUntil(this.destroyed$),
    ).subscribe(accounts => this.applyUserAccounts(accounts))
  }

  /** Login to personal or organization account */
  accountLogin(organization?: Organization) {
    if (this.state !== 'selectAccount' || this.authLoginRequested) return
    if (organization ? !this.organizations.some(member => member.id === organization.id) : !this.hasPersonalAccount) return
    this.authLoginRequested = true
    const orgID = organization?.id
    const storedOrigin = sessionStorage.getItem('authRedirectOrigin')
    let target = 'projects'
    if (storedOrigin && !storedOrigin.startsWith('//') && !storedOrigin.includes('\\') && !/[\x00-\x1f]/.test(storedOrigin)) {
      const destination = new URL(storedOrigin, window.location.origin + '/')
      if (destination.origin === window.location.origin && !/^[a-z][a-z0-9+.-]*:/i.test(storedOrigin)) target = storedOrigin
    }

    sessionStorage.removeItem('signInPolicyOrganization')
    this._authenticationService.rememberLoginAccount(organization
      ? { kind: 'organization', organizationId: organization.id }
      : { kind: 'personal' })
    sessionStorage.setItem('redirectDashboard', 'true')
    this._authenticationService.loginWithRedirect({ appState: { target }, authorizationParams: { organization: orgID } })
  }

  navigateToAuthLogin(fresh = false) {
    if (this.authLoginRequested) return
    if (this.hasReachedLocalPreviewAuthRedirectLimit()) {
      this.showLocalPreviewAuthRecovery()
      return
    }

    this.authLoginRequested = true
    this.recordLocalPreviewAuthRedirectStarted()
    this._authenticationService.loginWithRedirect({
      appState: { target: this.getAuthLoginTarget() },
      authorizationParams: { screen_hint: 'login', ...(fresh ? { prompt: 'login', max_age: 0 } : {}) },
    })
  }

  retryAccountLoad(): void {
    if (this.state !== 'accountRecovery' || this.accountLoadRequested) return
    this.signInError = ''
    this.state = 'loading'
    this.getUserAccountsOnce().pipe(
      catchError(err => this.handleAccountLoadError(err)),
    ).subscribe(accounts => this.applyUserAccounts(accounts))
  }

  retryAuthLogin(): void {
    this.authLoginRequested = false
    this.state = 'loading'
    this.clearLocalPreviewAuthRedirectMarker()
    if (this.isLocalPreview) {
      window.history.replaceState({}, '', '/login')
    }
    this.navigateToAuthLogin(true)
  }

  retryMigrationCompletion(): void {
    if (this.migrationCompletionPending || this.migrationRecovery !== 'retryable') return
    this.state = 'loading'
    this.migrationError = ''
    this.completeMigrationAndLoadAccounts().pipe(
      catchError(err => this.handleAccountLoadError(err)),
    ).subscribe(accounts => this.applyUserAccounts(accounts))
  }

  async continueWithPasswordAccount(): Promise<void> {
    if (this.migrationCompletionPending) return
    try {
      this._authenticationService.cancelPendingSsoMigration()
    } catch {
      this.migrationError = 'We could not clear this account connection. Sign out to continue safely.'
      return
    }
    this.migrationError = ''
    this.state = 'loading'
    let user: AuthenticationService['currentUser']
    try {
      user = await firstValueFrom(this.currentUser$)
    } catch {
      this.startFreshPasswordSignIn()
      return
    }
    if (!user?.sub?.startsWith('auth0|') || user['https://kartorium.com/authentication_strategy'] !== 'auth0') {
      this.startFreshPasswordSignIn()
      return
    }
    this.getUserAccountsOnce().pipe(
      catchError(err => this.handleAccountLoadError(err)),
    ).subscribe(accounts => this.applyUserAccounts(accounts))
  }

  resumeMigrationWithPassword(): void {
    if (this.migrationCompletionPending || this.migrationRecovery !== 'retryable') return
    this.state = 'loading'
    this.startFreshPasswordSignIn()
  }

  private startFreshPasswordSignIn(): void {
    this._authenticationService.loginWithRedirect({
      appState: { target: 'login' }, authorizationParams: {
        connection: 'Username-Password-Authentication', prompt: 'login', max_age: 0,
      },
    })
  }

  private completeMigrationAndLoadAccounts() {
    this.migrationCompletionPending = true
    return from(this._authenticationService.completePendingSsoMigration()).pipe(
      finalize(() => this.migrationCompletionPending = false),
      catchError((error: unknown) => {
        this.migrationError = error instanceof Error ? error.message : 'Account connection failed. Please sign in again.'
        if (error instanceof SsoMigrationRecoveryError) {
          this.migrationRecovery = error.recovery
          this.state = 'migrationRecovery'
        } else {
          this.state = 'authRecovery'
        }
        return EMPTY
      }),
      switchMap(() => this.getUserAccountsOnce()),
    )
  }

  signOutUnprovisionedAccount(): void {
    this._authenticationService.logout()
  }

  async migrateExistingAccount(): Promise<void> {
    if (this.migrationInProgress) return
    this.migrationInProgress = true
    this.migrationError = ''
    try {
      await this._authenticationService.migrateSsoToExistingPasswordAccount()
    } catch (error) {
      this.migrationError = error instanceof Error ? error.message : 'We could not connect your accounts. Please try again.'
    } finally {
      this.migrationInProgress = false
    }
  }

  signOut(): void {
    this._authenticationService.logout()
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
    this.localPreviewRetry?.unsubscribe()
    this.authErrorSubscription.unsubscribe()
  }

  private retryAuthLoginFromLocalPreview(): void {
    if (!this.isLocalPreview || this.state != 'loading' || this.migrationCompletionPending) return
    if (!this.authenticated && !this.authLoginRequested
      && this.getLocalPreviewAuthRedirectAttemptFromQuery() === 0) {
      this.state = 'authRecovery'
      return
    }
    if (this.authenticated) {
      this.getUserAccountsOnce().pipe(
        catchError(err => this.handleAccountLoadError(err)),
      ).subscribe(accounts => this.applyUserAccounts(accounts))
      return
    }
    if (this.hasReachedLocalPreviewAuthRedirectLimit()) {
      this.showLocalPreviewAuthRecovery()
      return
    }
    if (this.hasRecentLocalPreviewAuthRedirect()) return

    if (this.getLocalPreviewAuthRedirectAttemptFromQuery() > 0) {
      this.authLoginRequested = false
    }
    this.navigateToAuthLogin()
  }

  private getUserAccountsOnce() {
    return defer(() => {
      if (this.accountLoadRequested) return EMPTY
      this.accountLoadRequested = true
      return this._authenticationService.getUserAccounts()
    }).pipe(takeUntil(this.destroyed$))
  }

  private handleAccountLoadError(err: unknown) {
    if (policyDeniedOrganization(err)) {
      this.signInError = 'This organization requires another sign-in method. Return to sign in or contact support.'
      this.accountLoadRequested = false
      this.state = 'authRecovery'
      return EMPTY
    }
    const status = (err as { status?: number })?.status
    if (status === 403) {
      this.accountLoadRequested = false
      this.signInError = 'Account access could not be verified. Return to sign in or contact support.'
      this.state = 'authRecovery'
      return EMPTY
    }
    if (!isInteractiveAuthRequired(err) && status !== 401) {
      this.accountLoadRequested = false
      this.signInError = 'Account access is temporarily unavailable. Please retry.'
      this.state = isAccountDiscoveryUnavailable(err) ? 'accountRecovery' : 'authRecovery'
      return EMPTY
    }
    this.accountLoadRequested = false
    this.authLoginRequested = false
    this.navigateToAuthLogin()
    return EMPTY
  }

  private applyUserAccounts({ organizations, personalAccount, canMigrateExistingAccount }: UserAccounts): void {
    this.hasPersonalAccount = personalAccount !== null
    this.organizations = organizations.sort((a, b) => a.display_name.localeCompare(b.display_name))
    if (canMigrateExistingAccount) {
      this.state = 'migrateAccount'
      return
    }
    if (this.organizations.length > 0 || this.hasPersonalAccount) {
      this.state = 'selectAccount'
    } else if (this.capabilities.legacyStripe) {
      this.router.navigate(['signup'])
    } else {
      this.state = 'accountNotProvisioned'
    }
  }

  private recordLocalPreviewAuthRedirectStarted(): void {
    if (!this.isLocalPreview) return

    try {
      sessionStorage.setItem(LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_STARTED_KEY, String(Date.now()))
    } catch {
      // Session storage may be unavailable in hardened embedded browsers.
    }
  }

  private clearLocalPreviewAuthRedirectMarker(): void {
    try {
      sessionStorage.removeItem(LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_STARTED_KEY)
    } catch {
      // Session storage may be unavailable in hardened embedded browsers.
    }
  }

  private hasRecentLocalPreviewAuthRedirect(): boolean {
    const queryStartedAt = this.getLocalPreviewAuthRedirectStartedAtFromQuery()
    if (queryStartedAt != null) {
      return this.isLocalPreviewAuthRedirectRecent(queryStartedAt)
        && this.getLocalPreviewAuthRedirectAttemptFromQuery() >= LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_MAX_ATTEMPTS
    }

    try {
      const startedAt = Number(sessionStorage.getItem(LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_STARTED_KEY))
      if (!Number.isFinite(startedAt) || startedAt <= 0) return false
      const isRecent = this.isLocalPreviewAuthRedirectRecent(startedAt)
      if (!isRecent) this.clearLocalPreviewAuthRedirectMarker()
      return isRecent
    } catch {
      return false
    }
  }

  private hasReachedLocalPreviewAuthRedirectLimit(): boolean {
    return this.isLocalPreview
      && this.getLocalPreviewAuthRedirectAttemptFromQuery() >= LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_MAX_ATTEMPTS
  }

  private showLocalPreviewAuthRecovery(): void {
    this.state = 'authRecovery'
    this.clearLocalPreviewAuthRedirectMarker()
  }

  private getAuthLoginTarget(): string {
    if (this.policyOrganization) return `login?policy=1&organization=${this.policyOrganization}`
    if (!this.isLocalPreview) return 'login'

    const attempt = this.getLocalPreviewAuthRedirectAttemptFromQuery() + 1
    return `login?authRedirectStarted=${Date.now()}&authRedirectAttempt=${attempt}`
  }

  private getLocalPreviewAuthRedirectStartedAtFromQuery(): number | null {
    const startedAt = Number(new URLSearchParams(window.location.search).get('authRedirectStarted'))
    return Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null
  }

  private getLocalPreviewAuthRedirectAttemptFromQuery(): number {
    const attempt = Number(new URLSearchParams(window.location.search).get('authRedirectAttempt'))
    return Number.isFinite(attempt) && attempt > 0 ? attempt : 0
  }

  private isLocalPreviewAuthRedirectRecent(startedAt: number): boolean {
    return Date.now() - startedAt < LoginComponent.LOCAL_PREVIEW_AUTH_REDIRECT_GRACE_MS
  }

  private get isLocalPreview(): boolean {
    return !environment.production
      && isLocalAuthPreviewHost(window.location.hostname)
  }
}
